import React, { useContext, useState, useEffect, ChangeEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { message } from 'react-message-popup';
import { add, format, getDay } from 'date-fns';
import { Helmet } from 'react-helmet';

import Header from "../components/Header";
import Footer from "../components/Footer";
import ImageWithFallback from '../components/ImageWithFallback';
import { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import { useTranslation } from 'react-i18next';
import config from '../config/config.json';

// Interface for items in the cart
interface CartItem {
    productId: number | null;
    name: string | null;
    image: string | null;
    fallbackImage: string | null;
    price: number | null;
    cant: number | null;
}

function Cart() {
    const { t } = useTranslation("global");
    const { runtimeValues, setRuntimeValues } = useContext(RuntimeValuesContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // State for local calculations and UI control
    const [shipmentCalculate, setShipmentCalculate] = useState(false);
    const [cartItems, setCartItems] = useState(0);
    const [cartSubTotal, setCartSubTotal] = useState(0);
    const [cartTotal, setCartTotal] = useState(0);
    const [finalBusinessDate, setFinalBusinessDate] = useState('');
    const [title, setTitle] = useState<string>(`${t("pageTitle.cart")} ${t("seo.compl")} ${runtimeValues.country} - wicaglobalgear.com`);

    const productId = searchParams.get('productId');
    const productName = searchParams.get('productName');
    const productImage = searchParams.get('productImage');
    const fallbackImage = searchParams.get('fallbackImage');
    const productPrice = searchParams.get('productPrice');

    useEffect(() => {
        if (productId) {
            const productIdCleaned = parseInt(productId, 10);
            const productPriceCleaned = productPrice ? parseFloat(productPrice) : 0;
            setRuntimeValues(prevValues => {
                const existingItem = prevValues.cart.find(item => item.productId === productIdCleaned);
                let newCart: CartItem[];
                if (existingItem) {
                    newCart = prevValues.cart.map(item =>
                        item.productId === productIdCleaned ? { ...item, cant: (item.cant || 0) + 1 } : item
                    );
                } else {
                    const newItem: CartItem = { productId: productIdCleaned, name: productName, image: productImage, fallbackImage: fallbackImage, price: productPriceCleaned, cant: 1 };
                    newCart = [...prevValues.cart, newItem];
                }
                return { ...prevValues, cart: newCart };
            });
            navigate('/cart', { replace: true });
        }
    }, [productId, productName, productImage, fallbackImage, productPrice, setRuntimeValues, navigate]);
    
    useEffect(() => {
        setCartItems(runtimeValues.cart.reduce((acc, item) => acc + (item.cant ?? 0), 0));
        const subTotal = runtimeValues.cart.reduce((acc, item) => acc + ((item.price ?? 0) * (item.cant ?? 0)), 0);
        setCartSubTotal(subTotal);
        setCartTotal(subTotal + runtimeValues.shippingPrice);
        deliveryDate();
        let validations = "1";
        if (runtimeValues.shipping === "shipping") {
            if (runtimeValues.shippingAddressStreet.length < 6) { validations += ",0"; }
            if (runtimeValues.shippingCity.length < 3) { validations += ",1"; }
            if (runtimeValues.shippingState.length < 2) { validations += ",2"; }
            if (runtimeValues.shippingZipCode.length < 5) { validations += ",3"; }
            if (runtimeValues.shippingPhoneNumber.length < 10) { validations += ",4"; }
            if (validations === "1") {
                if (!shipmentCalculate) setShipmentCalculate(true);
            } else {
                if (shipmentCalculate) setShipmentCalculate(false);
            }
        }
    }, [runtimeValues]);

    useEffect(() => {
        if (shipmentCalculate) {
            handleShipment();
        }
    }, [shipmentCalculate]);

    useEffect(() => {
        deliveryDate().then(date => {
            setRuntimeValues(prev => ({
                ...prev,
                shippingAddressStreet: "", shippingAddressCompl: "", shippingCity: "",
                shippingState: "", shippingZipCode: "", shippingPhoneNumber: "",
                shippingPrice: 0, shippingETA: date,
            }));
        });
    }, [runtimeValues.countryCode]);
    
    const nationalHolidays = new Set(['01-01', '07-04', '11-11', '12-25']);

    const deliveryDate = async () => {
        let targetDate = add(new Date(), { days: 15 });
        while (true) {
            const dayOfWeek = getDay(targetDate);
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isHoliday = nationalHolidays.has(format(targetDate, 'MM-dd'));
            if (!isWeekend && !isHoliday) break;
            targetDate = add(targetDate, { days: 1 });
        }
        const formattedDate = format(targetDate, 'yyyy-MM-dd');
        setFinalBusinessDate(formattedDate);
        return formattedDate;
    };

    const handleCartDelete = () => {
        setRuntimeValues({ ...runtimeValues, cart: [] });
    };

    const handleCartItemDelete = (productIdToDelete: number) => {
        const newArray = runtimeValues.cart.map(item => {
            if (item.productId === productIdToDelete) {
                return { ...item, cant: (item.cant || 0) - 1 };
            }
            return item;
        }).filter(item => (item.cant || 0) > 0);
        setRuntimeValues({ ...runtimeValues, cart: newArray });
    };

    const handleChangeShipment = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setRuntimeValues({ ...runtimeValues, [name]: value });
    };

    const handleContinueToCheckout = () => {
        if (cartItems <= 0) {
            message.warning(t("cart.validationItems") || "Please add items to your cart.", 4000);
            return;
        }
        let validations = "";
        if (runtimeValues.shipping === "shipping") {
            if (runtimeValues.shippingAddressStreet.length < 6) { validations += `, ${t("cart.addressStreet") || 'Street Address'}`; }
            if (runtimeValues.shippingCity.length < 3) { validations += `, ${t("cart.addressCity") || 'City'}`; }
            if (runtimeValues.shippingState.length < 2) { validations += `, ${t("cart.addressState") || 'State'}`; }
            if (runtimeValues.shippingZipCode.length < 5) { validations += `, ${t("cart.addressZipCode") || 'Zip Code'}`; }
            if (runtimeValues.shippingPhoneNumber.length < 10) { validations += `, ${t("cart.addressPhoneNumber") || 'Phone Number'}`; }
            if (validations !== "") {
                const validationMessage = t("cart.validationMessage") || "Please complete the following fields";
                message.warning(`${validationMessage}: ${validations.substring(2)}`, 4000);
                return;
            }
            if (runtimeValues.shippingPrice === 0) {
                message.warning(t("cart.validationShipping") || "Please calculate shipping before proceeding.", 2500);
                return;
            }
        }
        navigate("/checkout");
    };

    const handleShippingOptions = (event: ChangeEvent<HTMLInputElement>) => {
        const newShippingType = event.target.value;
        if (newShippingType === "pickup") {
            setRuntimeValues({ ...runtimeValues, shipping: newShippingType, shippingPrice: 0, shippingETA: finalBusinessDate });
            setShipmentCalculate(false);
        } else {
            setRuntimeValues({ ...runtimeValues, shipping: newShippingType });
        }
    };
    
    const handleShipment = async () => {
        try {
            const response = await axios.post(`${config.backend.url}/api/dhl/shipmentrate`, {
                accountNumber: config.dhl.account_id,
                originCountryCode: "US", originPostalCode: "33133", originCityName: "Miami",
                destinationCountryCode: runtimeValues.countryCode,
                destinationCityName: runtimeValues.shippingCity,
                weight: 5, length: 15, width: 10, height: 2,
                plannedShippingDate: finalBusinessDate,
                isCustomsDeclarable: "false",
                unitOfMeasurement: "metric",
            });
            setRuntimeValues(prev => ({ ...prev, shippingPrice: response.data.price, shippingETA: formatDate(response.data.ETA) }));
            message.success("Shipment rate updated successfully.", 4000);
        } catch (error) {
            setRuntimeValues(prev => ({ ...prev, shippingPrice: 0, shippingETA: finalBusinessDate }));
            message.warning("Could not get shipment rate. Please check your address info.", 4000);
            console.error(error);
        }
    };

    function formatDate(dateIn: string): string {
        if (!dateIn || dateIn.length < 10) return finalBusinessDate;
        const date = new Date(dateIn);
        return format(date, 'MMM. d, yyyy');
    }

    let formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    switch (runtimeValues.currency) {
        case 'COP': formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }); break;
        case 'CLP': formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }); break;
        case 'PEN': formatter = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }); break;
        case 'PYG': formatter = new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }); break;
        case 'UYU': formatter = new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }); break;
        default: formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
    
    useEffect(() => {
        setTitle(`${t("pageTitle.cart")} ${t("seo.compl")} ${runtimeValues.country} - wicaglobalgear.com`);
        document.title = title;
    }, [runtimeValues.country, t, title]);

    return (
        <>
            <Helmet><title>{title}</title></Helmet>
            <div className="app-container">
                <Header />
                <div className="app-content">
                    <section className="sectionPageContent">
                        <h1>{t("pageTitle.cart")}</h1>
                        <div className="cartContainer">
                            <div className="cartItemContainerLeft">
                                {runtimeValues.cart.length === 0 ? (
                                    <span className="spanFilter">{t("cart.cartEmpty")}</span>
                                ) : (
                                    runtimeValues.cart.map((item) => {
                                        const primaryImagePath = item.image ? `images/${item.image}` : null;
                                        const fallbackImagePath = item.fallbackImage ? `images/${item.fallbackImage}` : null;
                                        return (
                                            <div className="cartItem" key={item.productId}>
                                                <div className="cartItemImageContainer"><ImageWithFallback primarySrc={primaryImagePath} fallbackSrc={fallbackImagePath} alt={item.name || ''} className='cartItemImage' /></div>
                                                <div className="cartItemName"><span>{item.name}</span></div>
                                                <div className="cartItemCant"><span>{item.cant}</span></div>
                                                <div className="cartItemPrice"><span>{formatter.format((item.price ?? 0) * (item.cant ?? 0))}</span></div>
                                                <div className="cartItemDelete"><img src="images/imgDel.png" className='cartItemImageDel' onClick={() => handleCartItemDelete(item.productId ?? 0)} alt="Delete" /></div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            <div className="cartItemContainerRight">
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.items")}:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter">{cartItems}</span></div>
                                </div>
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span className="spanFilter">Subtotal:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter">{formatter.format(cartSubTotal)}</span></div>
                                </div>
                                <div className="cartItemContainerRightItems" style={{ marginTop: '15px' }}><div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.shippingOptions")}:</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span className="spanItem">{t("cart.country")}: {runtimeValues.country}</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><ul>
                                    <li className="checkboxFilter"><input className='radio' type="radio" name="shipping" value="pickup" checked={runtimeValues.shipping === "pickup"} onChange={handleShippingOptions} /><span className="spanItemLower">{t("cart.pickUp")}</span></li>
                                    <li className="checkboxFilter"><input className='radio' type="radio" name="shipping" value="shipping" checked={runtimeValues.shipping === "shipping"} onChange={handleShippingOptions} /><span className="spanItemLower">{t("cart.shipping")}</span></li>
                                </ul></div></div>
                                {runtimeValues.shipping === "shipping" && (
                                    <div className="form">
                                        <div className="form-row"><div className="input-data"><input type="text" name="shippingAddressStreet" value={runtimeValues.shippingAddressStreet} onChange={handleChangeShipment} required /><div className="underline"></div><label>{t("cart.addressStreet")}</label></div></div>
                                        <div className="form-row"><div className="input-data"><input type="text" name="shippingAddressCompl" value={runtimeValues.shippingAddressCompl} onChange={handleChangeShipment} /><div className="underline"></div><label>{t("cart.addressStreetPart2")}</label></div></div>
                                        <div className="form-row"><div className="input-data"><input type="text" name="shippingCity" value={runtimeValues.shippingCity} onChange={handleChangeShipment} required /><div className="underline"></div><label>{t("cart.addressCity")}</label></div></div>
                                        <div className="form-row"><div className="input-data"><input type="text" name="shippingState" value={runtimeValues.shippingState} onChange={handleChangeShipment} required /><div className="underline"></div><label>{t("cart.addressState")}</label></div></div>
                                        <div className="form-row"><div className="input-data"><input type="number" name="shippingZipCode" value={runtimeValues.shippingZipCode} onChange={handleChangeShipment} required /><div className="underline"></div><label>{t("cart.addressZipCode")}</label></div></div>
                                        <div className="form-row"><div className="input-data"><input type="text" name="shippingPhoneNumber" value={runtimeValues.shippingPhoneNumber} onChange={handleChangeShipment} required /><div className="underline"></div><label>{t("cart.addressPhoneNumber")}</label></div></div>
                                    </div>
                                )}
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.shippingEstimated")}:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter">{runtimeValues.shippingETA || finalBusinessDate}</span></div>
                                </div>
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.shippingTotal")}:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter">{formatter.format(runtimeValues.shippingPrice)}</span></div>
                                </div>
                                <div className="cartItemContainerRightItems" style={{ borderTop: '1px solid #ccc', paddingTop: '10px', marginTop: '10px' }}>
                                    <div className="cartTotalItemTitle"><span className="spanFilter">Total:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter" style={{ fontWeight: 'bold' }}>{formatter.format(cartTotal)}</span></div>
                                </div>
                                <button className="plpButtonFocus" style={{marginTop: '20px', width: '100%'}} onClick={handleContinueToCheckout}>{t("general.completeCart")}</button>
                            </div>
                        </div>
                        <div className="product-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                            <button className="plpButton" onClick={handleCartDelete}>{t("general.deleteCart")}</button>
                            <Link to="/products" className="plpButtonFocus">{t("general.addMore")}</Link>
                        </div>
                    </section>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default Cart;